/**
 * PoiGroupFormWizard.jsx
 * Formulario de Grupo de POI em Etapas Premium
 *
 * 2 Passos:
 * 1. Basico - Nome, Descricao (obrigatorio)
 * 2. Revisao
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Grid, Typography } from '@mui/material';
import {
  Folder as GroupIcon,
  CheckCircle as ReviewIcon,
  Palette as ColorIcon,
} from '@mui/icons-material';

import { useTranslation } from '../../common/components/LocalizationProvider';
import { usePremiumTokens } from '../../common/theme/usePremiumTokens';

import PremiumWizard from '../../common/components/premium/PremiumWizard';
import PremiumInput from '../../common/components/premium/PremiumInput';
import { getWizardColors } from '../../common/components/premium/WizardStepTemplates';
import PoiColorPicker from '../PoiColorPicker';

// ============================================================================
// STEP 1: INFORMACOES BASICAS
// ============================================================================

const BasicInfoStep = ({ data, updateData, errors }) => {
  const t = useTranslation();
  const tokens = usePremiumTokens();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 9 }}>
          <PremiumInput
            label={`${t('sharedName')} *`}
            value={data.name || ''}
            onChange={(e) => updateData({ name: e.target.value })}
            error={!!errors?.name}
            helperText={errors?.name}
            autoFocus
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography
              variant="caption"
              sx={{
                color: tokens?.text?.secondary,
                fontSize: '0.75rem',
                fontWeight: 500,
              }}
            >
              {t('poiMarkerColor') || 'Cor do Grupo'}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                p: 1,
                borderRadius: tokens?.radius?.md || '8px',
                border: `1px solid ${tokens?.border?.subtle || 'rgba(255,255,255,0.1)'}`,
                backgroundColor: tokens?.surface?.elevated || 'rgba(255,255,255,0.03)',
              }}
            >
              <PoiColorPicker
                value={data.color || '#3B82F6'}
                onChange={(color) => updateData({ color })}
                size="medium"
              />
              <Box
                sx={{
                  flex: 1,
                  height: 24,
                  borderRadius: tokens?.radius?.sm || '4px',
                  backgroundColor: data.color || '#3B82F6',
                  opacity: 0.8,
                }}
              />
            </Box>
          </Box>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12 }}>
          <PremiumInput
            label={t('sharedDescription')}
            value={data.description || ''}
            onChange={(e) => updateData({ description: e.target.value })}
            multiline
            rows={3}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

// ============================================================================
// STEP 2: REVISAO
// ============================================================================

const ReviewStep = ({ data }) => {
  const t = useTranslation();
  const tokens = usePremiumTokens();
  const colors = getWizardColors(tokens);

  const InfoRow = ({ label, value }) => {
    if (!value && value !== 0) return null;
    return (
      <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="caption" sx={{ color: colors.textMuted, fontSize: '11px' }}>
          {label}
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: colors.textPrimary, fontWeight: 500, textAlign: 'right', maxWidth: '60%' }}
        >
          {value}
        </Typography>
      </Box>
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          p: 2,
          backgroundColor: colors.accentSubtle,
          borderRadius: tokens?.radius?.md || '8px',
          border: `1px solid ${colors.accent}30`,
        }}
      >
        <ReviewIcon sx={{ color: colors.accent }} />
        <Typography sx={{ color: colors.textPrimary }}>
          {t('wizardReviewConfirm')}
        </Typography>
      </Box>

      <Box
        sx={{
          backgroundColor: colors.surfaceElevated,
          borderRadius: tokens?.radius?.lg || '12px',
          border: `1px solid ${colors.borderSubtle}`,
          p: 2.5,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            color: colors.textPrimary,
            fontWeight: 600,
            textTransform: 'uppercase',
            fontSize: '11px',
            letterSpacing: '0.05em',
            mb: 2,
          }}
        >
          {t('sharedRequired')}
        </Typography>
        <InfoRow label={t('sharedName')} value={data.name} />
        <InfoRow label={t('sharedDescription')} value={data.description || '-'} />

        {/* Color Preview */}
        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="caption" sx={{ color: colors.textMuted, fontSize: '11px' }}>
            {t('poiMarkerColor') || 'Cor do Grupo'}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 24,
                height: 24,
                borderRadius: '50%',
                backgroundColor: data.color || '#3B82F6',
                border: `2px solid ${colors.borderSubtle}`,
              }}
            />
            <Typography
              variant="body2"
              sx={{ color: colors.textPrimary, fontWeight: 500, fontFamily: 'monospace', fontSize: '12px' }}
            >
              {data.color || '#3B82F6'}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const PoiGroupFormWizard = ({ poiGroup, onSave, onCancel }) => {
  const t = useTranslation();
  const navigate = useNavigate();

  const steps = [
    {
      label: t('wizardStepBasic'),
      title: t('sharedRequired'),
      description: t('wizardPoiGroupDescription') || 'Dados do grupo',
      icon: <GroupIcon sx={{ fontSize: 18 }} />,
      component: BasicInfoStep,
      validate: (data) => {
        const errors = {};
        if (!data.name?.trim()) errors.name = t('sharedRequired');
        return Object.keys(errors).length ? errors : true;
      },
      getSummary: (data) => [
        { label: t('sharedName'), value: data.name },
      ],
    },
    {
      label: t('wizardStepReview'),
      title: t('wizardReviewTitle'),
      description: t('wizardReviewDescription'),
      icon: <ReviewIcon sx={{ fontSize: 18 }} />,
      component: ReviewStep,
    },
  ];

  const handleComplete = async (data) => {
    try {
      const url = poiGroup?.id ? `/api/poigroups/${poiGroup.id}` : '/api/poigroups';
      const method = poiGroup?.id ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        const saved = await response.json();
        onSave?.(saved);
        navigate('/settings/poigroups');
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || t('errorGeneric'));
      }
    } catch (error) {
      console.error('Save error:', error);
    }
  };

  const handleCancel = () => {
    onCancel?.();
    navigate('/settings/poigroups');
  };

  const initialData = poiGroup || {
    name: '',
    description: '',
    color: '#3B82F6',
  };

  return (
    <PremiumWizard
      steps={steps}
      initialData={initialData}
      onComplete={handleComplete}
      onCancel={handleCancel}
      storageKey={poiGroup?.id ? null : 'poigroup-wizard-draft'}
      completeButtonText={poiGroup?.id ? t('sharedSave') : t('sharedAdd')}
      nextButtonText={t('wizardNext')}
      backButtonText={t('sharedBack')}
      cancelButtonText={t('sharedCancel')}
      showSummary
    />
  );
};

export default PoiGroupFormWizard;
